import { Grid } from '@mui/material';
import { SearchPredicate } from '@nx-smartmonkey/shared/domain';
import {
  HSpacer,
  RoutalPalette,
  RoutalSearchInput,
  RoutalSelect,
  RoutalSelectMultiSelectOption,
  VSpacer,
} from '@nx-smartmonkey/ui';
import _ from 'lodash';
import { useEffect, useState } from 'react';

import NoTranslationsPlaceholder from '../../../assets/img/admin-translations/no_translations.png';
import { sortDataByTableColumns, TableColumnsSortConfig } from '../../../context/shared/tableColumns';
import { Translation } from '../../../domain/translation/Translation';
import { TranslationDomain } from '../../../domain/translation/TranslationDomain';
import { useSearchTranslations } from '../../../hooks/backoffice/translations/useSearchTranslations';
import { useSearchTranslationsPredicates } from '../../../hooks/backoffice/translations/useSearchTranslationsPredicates';
import { useSetTranslationsColumns } from '../../../hooks/backoffice/translations/useSetTranslationsColumns';
import { useRetrieveUser } from '../../../hooks/user/useRetrieveUser';
import { DataTable } from '../../../react-components/data-table/DataTable';
import Text from '../../../react-components/Text';
import { AdministrationTranslationsHeader } from './AdministrationTranslationsHeader';
import { DeleteTranslationDialog } from './DeleteTranslationDialog';
import { getTranslationsColumns } from './TranslationsListColumns';

export const entriesStatus = [`all_texts`, `not_translated`, `translated`] as const;

export type EntriesStatusType = (typeof entriesStatus)[number];

export const BackofficeTranslations = () => {
  const { data: user } = useRetrieveUser();
  const { language, predicates, setPredicates } = useSearchTranslationsPredicates();

  const [sortTranslationsInfo, setSortTranslationsInfo] = useState<
    TableColumnsSortConfig<`identifier` | `original_value` | `value` | `created_at`>
  >({
    orderBy: `created_at`,
    order: `desc`,
  });

  const [searchParameters, setSearchParameters] = useState<{ limit: number; offset: number }>({
    offset: 0,
    limit: 100,
  });

  const { tableColumns } = useSetTranslationsColumns();

  const [translationToDelete, setTranslationToDelete] = useState<Translation | undefined>(undefined);
  const [textSearchField, setTextSearchField] = useState<`key` | `value`>(`value`);
  const isEmptyPredicate = predicates.find((p) => p.field === `is_empty`);
  const isEmpty = (isEmptyPredicate?.value as boolean) ?? null;

  const autoTranslatePredicate = predicates.find((p) => p.field === `auto_translate`);
  const isAutoTranslate = (autoTranslatePredicate?.value as boolean) ?? null;

  const searchTextPredicate = predicates.find((p) => p.field === `key`);
  const searchText = (searchTextPredicate?.value ?? ``) as string;

  const domainPredicate = predicates.find((p) => p.field === `domain`);
  const domain = (domainPredicate?.value ?? `all`) as TranslationDomain;

  const {
    data: translationsPagination,
    run: searchTranslations,
    isLoading,
  } = useSearchTranslations({
    language,
    offset: searchParameters.offset,
    limit: searchParameters.limit,
    sortBy: sortTranslationsInfo.orderBy,
    sortDirection: sortTranslationsInfo.order,
  });

  useEffect(() => {
    searchTranslations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParameters, sortTranslationsInfo]);

  const { docs, limit, page, pages, total } = translationsPagination || {};

  return (
    <Grid
      container
      direction="column"
      wrap="nowrap"
      style={{ backgroundColor: `white`, zIndex: 2, height: `100%`, overflowY: `auto` }}
    >
      <AdministrationTranslationsHeader onCreateTranslation={searchTranslations} />

      <Grid container direction="column" style={{ padding: `16px 22px`, height: `100%`, width: `100%` }} wrap="nowrap">
        <Grid
          style={{ paddingTop: `8px`, paddingBottom: `16px` }}
          container
          item
          justifyContent="space-between"
          direction="row"
          wrap="nowrap"
        >
          <Grid container item direction="row" wrap="nowrap" alignItems="center">
            <RoutalSelect
              id="domain-filter"
              containerStyle={{
                width: `230px`,
              }}
              popoverProps={{
                options: [`all`, `planner`, `drivers`, `customers`, `supervisors`].map(
                  (d, index): RoutalSelectMultiSelectOption => ({
                    key: d,
                    onClick: () => {
                      if (d === `all`) {
                        setPredicates(predicates.filter((p) => p.field !== `domain`));
                      } else {
                        setPredicates([
                          ...predicates.filter((p) => p.field !== `domain`),
                          SearchPredicate.create({
                            field: `domain`,
                            operator: `eq`,
                            type: `string`,
                            value: d,
                          }),
                        ]);
                      }
                    },
                    content: (
                      <Grid container direction="row" wrap="nowrap" alignItems="center" style={{ overflow: `hidden` }}>
                        <Text
                          tagName="span"
                          style={{
                            textAlign: `justify`,
                            whiteSpace: `nowrap`,
                            overflow: `hidden`,
                            width: `100%`,
                            textOverflow: `ellipsis`,
                            cursor: `pointer`,
                          }}
                          intlId={`administration.translations.domain.${d}`}
                        />
                      </Grid>
                    ),
                  })
                ),
              }}
            >
              <Text
                style={{
                  cursor: `pointer`,
                  width: `100%`,
                  textAlign: `justify`,
                }}
                intlId={`administration.translations.domain.${domain}`}
              />
            </RoutalSelect>

            <HSpacer medium />

            <RoutalSelect
              id="is_empty-filter"
              containerStyle={{
                width: `230px`,
              }}
              popoverProps={{
                options: [`all`, `translated`, `not_translated`].map(
                  (empty, index): RoutalSelectMultiSelectOption => ({
                    key: `is_empty-${empty}`,
                    onClick: () => {
                      if (empty === `all`) {
                        setPredicates(predicates.filter((p) => p.field !== `is_empty`));
                      } else {
                        setPredicates([
                          ...predicates.filter((p) => p.field !== `is_empty`),
                          SearchPredicate.create({
                            field: `is_empty`,
                            operator: `eq`,
                            type: `boolean`,
                            value: empty === `not_translated`,
                          }),
                        ]);
                      }
                    },
                    content: (
                      <Grid container direction="row" wrap="nowrap" alignItems="center" style={{ overflow: `hidden` }}>
                        <Text
                          tagName="span"
                          style={{
                            textAlign: `justify`,
                            whiteSpace: `nowrap`,
                            overflow: `hidden`,
                            width: `100%`,
                            textOverflow: `ellipsis`,
                            cursor: `pointer`,
                          }}
                          intlId={`administration.translations.${empty}`}
                        />
                      </Grid>
                    ),
                  })
                ),
              }}
            >
              <Text
                style={{
                  cursor: `pointer`,
                  width: `100%`,
                  textAlign: `justify`,
                }}
                intlId={`administration.translations.${
                  isEmpty === null ? `all` : isEmpty ? `not_translated` : `translated`
                }`}
              />
            </RoutalSelect>

            <HSpacer medium />

            <RoutalSelect
              id="is_automatic-filter"
              containerStyle={{
                width: `230px`,
              }}
              popoverProps={{
                options: [`all`, `automatic`, `manual`].map(
                  (automatic, index): RoutalSelectMultiSelectOption => ({
                    key: `is_empty-${automatic}`,
                    onClick: () => {
                      if (automatic === `all`) {
                        setPredicates(predicates.filter((p) => p.field !== `auto_translate`));
                      } else {
                        setPredicates([
                          ...predicates.filter((p) => p.field !== `auto_translate`),
                          SearchPredicate.create({
                            field: `auto_translate`,
                            operator: `eq`,
                            type: `boolean`,
                            value: automatic === `automatic` ? true : false,
                          }),
                        ]);
                      }
                    },
                    content: (
                      <Grid container direction="row" wrap="nowrap" alignItems="center" style={{ overflow: `hidden` }}>
                        <Text
                          tagName="span"
                          style={{
                            textAlign: `justify`,
                            whiteSpace: `nowrap`,
                            overflow: `hidden`,
                            width: `100%`,
                            textOverflow: `ellipsis`,
                            cursor: `pointer`,
                          }}
                          intlId={`administration.translations.${automatic}`}
                        />
                      </Grid>
                    ),
                  })
                ),
              }}
            >
              <Text
                style={{
                  cursor: `pointer`,
                  width: `100%`,
                  textAlign: `justify`,
                }}
                intlId={`administration.translations.${
                  isAutoTranslate === null ? `all` : isAutoTranslate ? `automatic` : `manual`
                }`}
              />
            </RoutalSelect>
          </Grid>

          <Grid item container direction="row" justifyContent="flex-end" alignItems="center" wrap="nowrap">
            <RoutalSearchInput
              inputContainerStyles={{
                width: `260px`,
              }}
              fieldValue={searchText}
              onChange={_.debounce((text: any) => {
                setPredicates([
                  ...predicates.filter((p) => p.field !== `key` && p.field !== `value`),
                  ...(text
                    ? [
                        SearchPredicate.create({
                          field: textSearchField,
                          operator: `contains`,
                          type: `string`,
                          value: text,
                        }),
                      ]
                    : []),
                ]);
              }, 300)}
              searchLive
            />

            <HSpacer small />

            <Grid item container direction="column" style={{ width: `30px` }}>
              <Text
                variant="label"
                onClick={() => setTextSearchField(`value`)}
                style={{
                  cursor: `pointer`,
                  ...(textSearchField === `value` ? { color: RoutalPalette.primary40 } : {}),
                }}
              >
                value
              </Text>
              <Text
                variant="label"
                onClick={() => setTextSearchField(`key`)}
                style={{ cursor: `pointer`, ...(textSearchField === `key` ? { color: RoutalPalette.primary40 } : {}) }}
              >
                key
              </Text>
            </Grid>
          </Grid>
        </Grid>

        {isLoading || (total !== undefined && total > 0) ? (
          <Grid item container style={{ overflowX: `auto`, overflowY: `hidden`, height: `100%`, width: `100%` }}>
            {isLoading || (docs ?? []).length > 0 ? (
              <Grid container style={{ width: `100%`, height: `100%`, position: `relative` }}>
                <div style={{ height: `100%`, width: `100%`, overflowX: `auto`, position: `absolute` }}>
                  <DataTable
                    loading={isLoading}
                    showCheckboxes={false}
                    docs={sortDataByTableColumns({
                      data: docs ?? [],
                      tableColumns,
                      sortInfo: sortTranslationsInfo,
                    }).map((translation: Translation) => ({ doc: translation }))}
                    limit={limit}
                    page={page}
                    pages={pages}
                    total={total ?? 0}
                    noFooter={isLoading}
                    onChangePage={({ offset, limit }) =>
                      setSearchParameters({
                        ...searchParameters,
                        offset,
                        limit,
                      })
                    }
                    hover
                    sortBy={sortTranslationsInfo.orderBy}
                    sortDirection={sortTranslationsInfo.order}
                    onColumnSort={(column: any, order: `asc` | `desc`) => {
                      setSortTranslationsInfo({ order, orderBy: column });
                    }}
                    columns={[
                      ...getTranslationsColumns({
                        user,
                        tableColumns,
                        setTranslationToDelete,
                        onEditTranslation: searchTranslations,
                      }),
                    ]}
                  />
                </div>
              </Grid>
            ) : (
              <Grid
                container
                direction="column"
                alignItems="center"
                style={{
                  height: `100%`,
                  padding: `32px`,
                }}
              >
                <Text variant="body-large" intlId="no_results" />
              </Grid>
            )}
          </Grid>
        ) : (
          <Grid
            container
            direction="column"
            justifyContent="center"
            alignItems="center"
            style={{ height: `100%`, width: `100%`, marginTop: `50px` }}
          >
            <img src={NoTranslationsPlaceholder} alt="" draggable="false" width="470" />
            <VSpacer medium />
            <Text variant="body-medium" intlId="administration.translations.no_translations" />
          </Grid>
        )}

        {user?.isRoot() && translationToDelete !== undefined ? (
          <DeleteTranslationDialog
            translation={translationToDelete}
            onClose={() => {
              setTranslationToDelete(undefined);
            }}
            onDeleteTranslation={searchTranslations}
          />
        ) : null}
      </Grid>
      <div id="copy-translation-key-helper" />
    </Grid>
  );
};
